<script lang="ts" setup>
  import { computed } from 'vue';
  import { useNotification } from '../../composables/use-notification';
  import {
    notificationWrapperClass,
    notificationBackgroundByVariantClass,
    notificationLeftSideClass,
    notificationContentClass,
    notificationIconClass,
    dismissIconClass,
    notificationDismissableClass,
    statusIconWrapperClass,
    statusIconBackgroundClass,
  } from './Notification.css';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import {
    NotificationVariant,
    type NotificationMessage,
  } from '../../store/notification.types';
  import {
    Button,
    ButtonSize,
    ButtonVariant,
    FontIcon,
  } from '@tundr/ds-components';
  import { rowCenterClass } from '@tundr/theme';
  import { useLocale } from '@tundr/i18n';

  const { t } = useLocale();
  const notification = useNotification();

  const lastNotification = computed(() => {
    return notification.getLastNotification.value;
  });

  const notificationWrapperClasses = computed(() => {
    return [
      notificationWrapperClass,
      {
        [notificationDismissableClass]: lastNotification.value?.dismissable,
      },
      notificationBackgroundByVariantClass[
        lastNotification.value?.variant || NotificationVariant.WARNING
      ],
    ];
  });

  const statusIconClasses = computed(() => [
    statusIconWrapperClass,
    statusIconBackgroundClass[
      lastNotification.value?.variant || NotificationVariant.WARNING
    ],
  ]);

  const iconByVariant = computed(() => {
    const iconsMap = {
      [NotificationVariant.ERROR]: TUNDR_ICONS.ERROR,
      [NotificationVariant.WARNING]: TUNDR_ICONS.EXCLAMATION_TRIANGLE,
      [NotificationVariant.SUCCESS]: TUNDR_ICONS.CHECK_CIRCLE,
      [NotificationVariant.INFO]: TUNDR_ICONS.INFO,
    };
    return iconsMap[
      lastNotification.value?.variant || NotificationVariant.INFO
    ];
  });

  const onDismiss = () => {
    notification.removeNotification(lastNotification.value!.key);
  };

  const normalizeButton = (
    button: NonNullable<NotificationMessage['actions']>[number],
  ) => {
    const { label, ...rest } = button;
    return {
      ...rest,
      size: rest.size ?? ButtonSize.SM,
      variant: rest.variant ?? ButtonVariant.Secondary,
    };
  };
</script>

<template>
  <Transition :appear="true" name="slide-down">
    <div v-if="lastNotification" :class="notificationWrapperClasses">
      <Button
        v-if="lastNotification.dismissable"
        :variant="ButtonVariant.Icon"
        is-ghost
        :class="dismissIconClass"
        :icon-name="TUNDR_ICONS.CLOSE"
        :aria-label="t('common:actions.close')"
        @click="onDismiss"
      />
      <div :class="notificationLeftSideClass">
        <div :class="statusIconClasses">
          <FontIcon :class="notificationIconClass" :icon-name="iconByVariant" />
        </div>

        <div :class="notificationContentClass">
          <strong>{{ lastNotification.title }}</strong>
          <span>{{ lastNotification.message }}</span>
        </div>
      </div>
      <div v-if="lastNotification.actions" :class="rowCenterClass">
        <Button
          v-for="button in lastNotification.actions"
          :key="button.label"
          v-bind="normalizeButton(button)"
          >{{
            button.label
          }}</Button
        >
      </div>
    </div>
  </Transition>
</template>

<style scoped>
  .slide-down-enter-active,
  .slide-down-leave-active {
    transition: all 0.4s ease;
  }
  .slide-down-enter-from,
  .slide-down-leave-to {
    transform: translateY(-20px);
  }
</style>
